import type { CurrencyIncome } from '@sea-snag/shared';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Skeleton } from '@sea-snag/ui';
import { useState } from 'react';

import { useCanSeeAdmin } from '@/hooks/use-can-see-admin';

import { useIncome, useSetTaxRate, useUpdateOwnerShare } from '../../hooks/use-income';
import { IncomeMonthlyChart } from './income-monthly-chart';
import { PerBoatIncomeTable } from './per-boat-income-table';

interface IncomeOverviewProps {
  orgPid: string;
  year: number;
  boatPid: string;
}

function money(currency: string, value: number): string {
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

export function IncomeOverview({ orgPid, year, boatPid }: IncomeOverviewProps) {
  const canEdit = useCanSeeAdmin();

  const { data, isLoading } = useIncome(orgPid, year, boatPid);
  const updateShare = useUpdateOwnerShare(orgPid, year, boatPid);
  const setTaxRate = useSetTaxRate(orgPid);
  const [currencyIdx, setCurrencyIdx] = useState(0);
  const [taxDraft, setTaxDraft] = useState<string | null>(null);

  if (isLoading || !data) return <Skeleton className="h-[600px] w-full" />;
  if (data.byCurrency.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          No income for {year} yet. Bookings appear here once they sync.
        </CardContent>
      </Card>
    );
  }

  const cur: CurrencyIncome = data.byCurrency[Math.min(currencyIdx, data.byCurrency.length - 1)]!;
  const coverage =
    cur.agent.totalBookings > 0
      ? Math.round((cur.agent.bookingsWithCommission / cur.agent.totalBookings) * 100)
      : 0;

  const taxDirty = taxDraft !== null && Number(taxDraft) !== data.taxRatePct;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Charter tax</span>
        {canEdit ? (
          <>
            <Input
              type="number"
              min={0}
              max={100}
              step="0.01"
              aria-label="Charter tax rate"
              className="h-7 w-20 text-right text-xs"
              value={taxDraft ?? String(data.taxRatePct)}
              onChange={(e) => setTaxDraft(e.target.value)}
            />
            <span className="text-muted-foreground">%</span>
            {taxDirty && (
              <Button
                size="sm"
                className="h-7 px-2 text-xs"
                disabled={setTaxRate.isPending}
                onClick={() =>
                  setTaxRate.mutate(Number(taxDraft), { onSuccess: () => setTaxDraft(null) })
                }
              >
                Save
              </Button>
            )}
          </>
        ) : (
          <span className="font-medium">{data.taxRatePct}%</span>
        )}
        <span className="text-xs text-muted-foreground">
          included in gross, removed before the owner split
        </span>
      </div>

      {data.byCurrency.length > 1 && (
        <div className="flex gap-2">
          {data.byCurrency.map((c, i) => (
            <Button
              key={c.currency}
              variant={i === currencyIdx ? 'default' : 'outline'}
              size="sm"
              aria-pressed={i === currencyIdx}
              onClick={() => setCurrencyIdx(i)}
            >
              {c.currency}
            </Button>
          ))}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <SummaryCard
          title="Charter (net)"
          value={money(cur.currency, cur.charter.net)}
          sub={`${cur.charter.bookings} bookings · gross ${money(
            cur.currency,
            cur.charter.gross
          )} incl. ${data.taxRatePct}% tax`}
        />
        <SummaryCard
          title="Agent (commission)"
          value={money(cur.currency, cur.agent.commission)}
          sub={`on ${coverage}% of bookings`}
        />
        <SummaryCard
          title="Owner"
          value={money(cur.currency, cur.charter.ownerTotal)}
          sub={
            cur.boatsMissingShare > 0
              ? `${cur.boatsMissingShare} boat(s) need a share`
              : 'all boats configured'
          }
        />
        <SummaryCard
          title="Management"
          value={money(cur.currency, cur.charter.managementTotal)}
          sub="after owner share"
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Monthly income</CardTitle>
        </CardHeader>
        <CardContent>
          <IncomeMonthlyChart monthly={cur.monthly} currency={cur.currency} />
        </CardContent>
      </Card>

      <PerBoatIncomeTable
        boats={cur.perBoat}
        currency={cur.currency}
        year={year}
        canEdit={canEdit}
        isPending={updateShare.isPending}
        onSaveShare={(bp, sharePct) =>
          updateShare.mutate({ boatPid: bp, data: { year, sharePct } })
        }
      />

      {/* Per-owner rollup */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Per-owner income</CardTitle>
        </CardHeader>
        <CardContent>
          {cur.perOwner.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No owner income yet — set a share on the boats above.
            </p>
          ) : (
            <ul className="divide-y">
              {cur.perOwner.map((o) => (
                <li
                  key={o.ownerPid ?? 'unassigned'}
                  className="flex items-center justify-between py-2"
                >
                  <span className="font-medium">{o.name}</span>
                  <span className="tabular-nums">{money(cur.currency, o.ownerIncome)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryCard({ title, value, sub }: { title: string; value: string; sub: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{title}</div>
        <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
        <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
      </CardContent>
    </Card>
  );
}
